/**
 * The meeting as a plan: what it is, when, where, what is on the agenda and who
 * is expected.
 *
 * Filled in before the meeting, then corrected after it — attendance and the time
 * of conclusion are only knowable on the day, so the same form serves both and
 * nothing is required that a person could not yet know.
 *
 * Directors are picked from the company's own record, never typed. The point of
 * the Register of Directors and KMP is that this list is already known, and a
 * hand-typed DIN is a DIN that will not match anything later.
 */

import { useEffect, useMemo, useState } from 'react';
import { TbPlus, TbTrash, TbGripVertical, TbWand } from 'react-icons/tb';
import Button from '../common/Button';
import { labelClass, inputClass } from '../common/FormControls';

const MEETING_TYPES = [
  { value: 'board', label: 'Board Meeting' },
  { value: 'committee', label: 'Committee Meeting' },
  { value: 'agm', label: 'Annual General Meeting' },
  { value: 'egm', label: 'Extra-Ordinary General Meeting' },
];

const MODES = [
  { value: 'physical', label: 'Physical' },
  { value: 'video', label: 'Video conferencing' },
  { value: 'hybrid', label: 'Hybrid' },
];

const ATTENDANCE = [
  { value: 'present', label: 'Present' },
  { value: 'video', label: 'Present by video' },
  { value: 'leave_of_absence', label: 'Leave of absence' },
  { value: 'absent', label: 'Absent' },
];

/**
 * The form is long — details, agenda, attendance — and at full width it still
 * scrolls. Headings turn that into three recognisable tasks instead of one
 * undifferentiated column of inputs.
 */
function Section({ title, hint, children }) {
  return (
    <section>
      <div className="mb-3 border-b border-[#e8e2d5] pb-1.5">
        <h3 className="text-[13px] font-bold tracking-[0.3px] text-[#2c2a24]">{title}</h3>
        {hint && <p className="mt-0.5 text-[11px] text-[#8a8172]">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

const isoDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

export default function MeetingForm({ meeting, directors, onSubmit, formId = 'meeting-form' }) {
  const [form, setForm] = useState(() => ({
    title: meeting?.title || '',
    meetingType: meeting?.meetingType || 'board',
    committeeName: meeting?.committeeName || '',
    meetingNumber: meeting?.meetingNumber || '',
    heldOn: isoDate(meeting?.heldOn) || new Date().toISOString().slice(0, 10),
    startTime: meeting?.startTime || '',
    endTime: meeting?.endTime || '',
    venue: meeting?.venue || '',
    mode: meeting?.mode || 'physical',
  }));

  const [agenda, setAgenda] = useState(
    () => (meeting?.agendaItems || []).map((a) => ({ title: a.title, isUpsi: !!a.isUpsi })),
  );
  const [newAgenda, setNewAgenda] = useState('');

  // Attendance keyed by the director's id on the company record, so re-opening
  // the form re-selects the same people.
  const [attendance, setAttendance] = useState(() => {
    const seeded = {};
    (meeting?.attendees || []).forEach((a) => {
      const key = String(a.folioId || a.din || a.name);
      seeded[key] = { selected: true, attendance: a.attendance, isChairperson: !!a.isChairperson, leaveReason: a.leaveReason || '' };
    });
    return seeded;
  });

  useEffect(() => {
    if (form.meetingType !== 'committee' && form.committeeName) {
      setForm((f) => ({ ...f, committeeName: '' }));
    }
  }, [form.meetingType, form.committeeName]);

  const keyOf = (d) => String(d.folioId || d.din || d.name);
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleDirector = (d) => {
    const key = keyOf(d);
    setAttendance((prev) => {
      const current = prev[key];
      if (current?.selected) return { ...prev, [key]: { ...current, selected: false } };
      return { ...prev, [key]: { selected: true, attendance: 'present', isChairperson: false, leaveReason: '' } };
    });
  };

  const setAttendanceField = (key, field, value) =>
    setAttendance((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  /** Exactly one chair — selecting a new one releases the previous. */
  const setChair = (key) =>
    setAttendance((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => { next[k] = { ...v, isChairperson: k === key }; });
      return next;
    });

  const selected = useMemo(
    () => directors.filter((d) => attendance[keyOf(d)]?.selected),
    [directors, attendance],
  );

  const presentCount = selected.filter(
    (d) => ['present', 'video'].includes(attendance[keyOf(d)]?.attendance),
  ).length;

  const addAgenda = () => {
    const title = newAgenda.trim();
    if (!title) return;
    setAgenda((items) => [...items, { title, isUpsi: false }]);
    setNewAgenda('');
  };

  /**
   * Fill the form with a plausible board meeting, for testing the pipeline
   * without typing it out each time.
   *
   * Development only, and deliberately not a set of default values. Times in
   * particular must never be pre-filled in production: a clock time nobody
   * stated is a fabricated fact on a statutory record, which is the same reason
   * the drafting pipeline prints [START TIME] rather than inventing one.
   */
  const fillSample = () => {
    const now = new Date();
    const fy = now.getMonth() >= 3
      ? `${now.getFullYear()}-${String(now.getFullYear() + 1).slice(2)}`
      : `${now.getFullYear() - 1}-${String(now.getFullYear()).slice(2)}`;

    setForm({
      title: `Board Meeting 03/${fy}`,
      meetingType: 'board',
      committeeName: '',
      meetingNumber: `BM/03/${fy}`,
      heldOn: now.toISOString().slice(0, 10),
      startTime: '11:00 A.M.',
      endTime: '12:30 P.M.',
      venue: 'Registered office of the Company',
      mode: 'physical',
    });

    setAgenda([
      { title: 'Confirmation of Minutes of the Previous Meeting', isUpsi: false },
      { title: 'Adoption of Audited Financial Statements', isUpsi: true },
      { title: 'Approval of borrowing from the Company’s bankers', isUpsi: false },
      { title: 'Any Other Business with Permission of Chair', isUpsi: false },
    ]);

    // Everyone present, the first director in the chair — the ordinary case, and
    // enough to satisfy quorum on any board.
    const seeded = {};
    directors.forEach((d, i) => {
      seeded[keyOf(d)] = {
        selected: true,
        attendance: i === 1 ? 'video' : 'present',
        isChairperson: i === 0,
        leaveReason: '',
      };
    });
    setAttendance(seeded);
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      agendaItems: agenda,
      attendees: selected.map((d) => {
        const a = attendance[keyOf(d)];
        return {
          folioId: d.folioId,
          name: d.name,
          din: d.din,
          designation: d.designation,
          attendance: a.attendance,
          isChairperson: a.isChairperson,
          leaveReason: a.leaveReason,
        };
      }),
    });
  };

  return (
    <form id={formId} onSubmit={submit} className="space-y-5">
      {/* Development only. Never shipped to a real user: the times it writes are
          invented, and an invented time on a statutory record is a false one. */}
      {process.env.NODE_ENV === 'development' && !meeting && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-[#c4bcab] bg-[#faf8f3] px-3 py-2">
          <span className="text-[11px] text-[#8a8172]">
            Testing? Fill this in with a sample board meeting.
          </span>
          <Button type="button" variant="ink" size="xs" icon={TbWand} onClick={fillSample}>
            Fill sample data
          </Button>
        </div>
      )}

      <Section title="Details">
        <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="mtg-title">Title</label>
          <input
            id="mtg-title" className={inputClass} value={form.title} onChange={set('title')}
            placeholder="Board Meeting 03/2026-27" required
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="mtg-type">Type</label>
          <select id="mtg-type" className={inputClass} value={form.meetingType} onChange={set('meetingType')}>
            {MEETING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {form.meetingType === 'committee' ? (
          <div>
            <label className={labelClass} htmlFor="mtg-committee">Committee</label>
            <input
              id="mtg-committee" className={inputClass} value={form.committeeName}
              onChange={set('committeeName')} placeholder="Audit Committee"
            />
          </div>
        ) : (
          <div>
            <label className={labelClass} htmlFor="mtg-number">Meeting number</label>
            <input
              id="mtg-number" className={inputClass} value={form.meetingNumber}
              onChange={set('meetingNumber')} placeholder="BM/03/2026-27"
            />
          </div>
        )}

        <div>
          <label className={labelClass} htmlFor="mtg-date">Date</label>
          <input id="mtg-date" type="date" className={inputClass} value={form.heldOn} onChange={set('heldOn')} required />
        </div>

        <div>
          <label className={labelClass} htmlFor="mtg-mode">Mode</label>
          <select id="mtg-mode" className={inputClass} value={form.mode} onChange={set('mode')}>
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="mtg-start">Commenced at</label>
          <input id="mtg-start" className={inputClass} value={form.startTime} onChange={set('startTime')} placeholder="11:00 A.M." />
        </div>

        <div>
          {/* SS-1 requires the time of conclusion, and approval is refused
              without it — but it cannot be known before the meeting ends. */}
          <label className={labelClass} htmlFor="mtg-end">Concluded at</label>
          <input id="mtg-end" className={inputClass} value={form.endTime} onChange={set('endTime')} placeholder="12:30 P.M." />
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="mtg-venue">Venue</label>
          <input id="mtg-venue" className={inputClass} value={form.venue} onChange={set('venue')} placeholder="Registered office" />
        </div>
        </div>
      </Section>

      {/* ── Agenda ───────────────────────────────────────────────────────── */}
      <Section title="Agenda" hint="In the order the items will be taken. Tick UPSI where an item involves unpublished price sensitive information.">
        <div className="space-y-2">
          {agenda.map((item, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-[#e8e2d5] bg-[#faf8f3] px-3 py-2">
              <TbGripVertical className="shrink-0 text-[#c4bcab]" size={14} />
              <span className="flex-1 text-[13px] text-[#2c2a24]">{item.title}</span>
              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-[11px] text-[#6b6252]" title="Involves unpublished price sensitive information">
                <input
                  type="checkbox" checked={item.isUpsi}
                  onChange={(e) => setAgenda((items) => items.map((it, j) => (j === i ? { ...it, isUpsi: e.target.checked } : it)))}
                />
                UPSI
              </label>
              <button
                type="button" aria-label={`Remove ${item.title}`}
                className="shrink-0 text-[#b0a695] hover:text-[#c4503f]"
                onClick={() => setAgenda((items) => items.filter((_, j) => j !== i))}
              >
                <TbTrash size={15} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-2 flex gap-2">
          <input
            className={inputClass} value={newAgenda} onChange={(e) => setNewAgenda(e.target.value)}
            placeholder="Add an agenda item…"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAgenda(); } }}
          />
          <Button type="button" variant="ink" size="sm" icon={TbPlus} onClick={addAgenda}>Add</Button>
        </div>
      </Section>

      {/* ── Attendance ───────────────────────────────────────────────────── */}
      <Section
        title="Attendance"
        hint={`${presentCount} of ${directors.length} present. Attendance and the time of conclusion are only knowable on the day — come back and correct them after the meeting.`}
      >

        {directors.length === 0 ? (
          <p className="text-[12px] text-[#8a8172]">
            No directors on this company&rsquo;s record. Add them to the company before minuting a meeting.
          </p>
        ) : (
          <div className="space-y-2">
            {directors.map((d) => {
              const key = keyOf(d);
              const row = attendance[key];
              return (
                <div key={key} className="rounded-lg border border-[#e8e2d5] bg-white px-3 py-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex flex-1 cursor-pointer items-center gap-2">
                      <input type="checkbox" checked={!!row?.selected} onChange={() => toggleDirector(d)} />
                      <span className="text-[13px] font-semibold text-[#2c2a24]">{d.name}</span>
                      {d.din && <span className="text-[10px] text-[#8a8172]">DIN {d.din}</span>}
                    </label>

                    {row?.selected && (
                      <>
                        <select
                          className="rounded-md border border-[#ddd6c6] bg-white px-2 py-1 text-[12px]"
                          value={row.attendance}
                          aria-label={`Attendance for ${d.name}`}
                          onChange={(e) => setAttendanceField(key, 'attendance', e.target.value)}
                        >
                          {ATTENDANCE.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                        <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-[#6b6252]">
                          <input type="radio" name="chairperson" checked={!!row.isChairperson} onChange={() => setChair(key)} />
                          Chair
                        </label>
                      </>
                    )}
                  </div>

                  {row?.selected && row.attendance === 'leave_of_absence' && (
                    <input
                      className="mt-2 w-full rounded-md border border-[#ddd6c6] px-2 py-1 text-[12px]"
                      placeholder="Reason leave was granted"
                      value={row.leaveReason}
                      onChange={(e) => setAttendanceField(key, 'leaveReason', e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

    </form>
  );
}
